import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import AutocompleteSelect from 'chameleon/ui-stack/ui/forms/autocomplete-select';
import { Icon, LoadingSpinner } from 'legacy-packages/ft-cb';
import Button from 'chameleon/ui-stack/ui/base/button';
import {
  extractDistrictSchoolStudentHierarchyData,
  getPeriodStartDate,
  getRequiredEducationPeriods,
  transformCategoryData,
  transFormClassData,
  transformDistrictData,
  transformEducationPeriod,
  transformSchoolSet,
  transFormStudentsData,
} from 'chameleon/src/components/TrendReportingContainer/utils';
import {
  getCategories,
  getDistrictSchoolStudentHierarchy,
} from 'chameleon/src/components/TrendReportingContainer/client';
import dateTimeFormat from 'chameleon/ui-stack/utilities/date';
import DatePickerWrapper from './components/DatePickerWrapper';
import { questionTypes } from './mocks';
import FiltersWithPopover from '../FiltersWithPopover';
import useDynamicColspan from '../hooks/useDynamicColspan';

import './style.scss';

const Filters = (props) => {
  const {
    currentEducationPeriod,
    educationPeriods,
    fetchTrendData,
    subjectId,
    setSelectedCurrCategory,
    isStudent,
    router,
    updateAssessmentTypeParams,
    checkedItems,
    setCheckedItems,
    schoolSet,
    teacherSubjects,
    courseOutline,
    setSelectedAcademicYear,
    setSelectedSectionName,
    setSelectedStudentName,
  } = props;

  const transformedSchoolList = schoolSet.map((school) => ({
    value: Number(school.initId),
    label: school.name,
  }));
  const schoolIdToDistrictIdMap = schoolSet.reduce((acc, school) => {
    acc[Number(school.initId)] = school.parentId;
    return acc;
  }, {});

  // Inialize State
  const schoolInitialState = { label: 'All Schools', value: null };
  const [districtSelected, setDistrictSelected] = useState({});
  const [
    districtSchoolStudentHierarchyData,
    setDistrictSchoolStudentHierarchyData,
  ] = useState({});

  const { id: currentEducationPeriodId } = currentEducationPeriod;
  const requiredEducationPeriods = getRequiredEducationPeriods(
    currentEducationPeriodId,
    educationPeriods,
    courseOutline?.earliestReportingEp || 0,
  );

  const extractedDistrictSchoolStudentHierarchyData =
    extractDistrictSchoolStudentHierarchyData(
      districtSchoolStudentHierarchyData,
      districtSelected?.value,
    );

  const availableEducationPeriods = new Set(
    teacherSubjects
      .filter((subject) => subject.id === subjectId)
      .flatMap((subject) => subject.educationPeriods)
      .map((period) => period.id),
  );

  const filteredEducationPeriods = requiredEducationPeriods.filter((item) =>
    availableEducationPeriods.has(item.id),
  );
  const academicYears = transformEducationPeriod(filteredEducationPeriods);
  let schoolList = transformSchoolSet(
    schoolInitialState,
    extractedDistrictSchoolStudentHierarchyData,
    districtSelected,
  );

  schoolList =
    isStudent && schoolSet.length > 1 ? transformedSchoolList : schoolList;
  const academicYearsLength = academicYears.length;
  const schoolListLength = schoolList?.length;
  const currentAcademicYear = {
    label: currentEducationPeriod.displayValue,
    value: currentEducationPeriod.id,
  };
  const showAcademicYearsDropdown = !isStudent && academicYearsLength > 1;
  const showSchoolDropdown =
    (!isStudent && schoolListLength > 2) || schoolSet.length > 1;
  const defaultAcademicYear = !currentAcademicYear
    ? academicYearsLength > 0
      ? academicYears[0]
      : null
    : currentAcademicYear;
  const sectionInitialState = { label: 'All Sections', value: null };
  const studentsInitialState = { label: 'All Students', value: null };

  const [previousEducationPeriod, setPreviousEducationPeriod] = useState(0);
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [sectionSelected, setSectionSelected] = useState(sectionInitialState);
  const [schoolSelected, setSchoolSelected] = useState(schoolInitialState);
  const [studentSelected, setStudentSelected] = useState(studentsInitialState);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [questionTypeSelected, setQuestionTypeSelected] = useState(
    questionTypes[0],
  );
  const [dateFrom, setDateFrom] = useState(
    getPeriodStartDate(requiredEducationPeriods[0].contentAvailableAt),
  );
  const [dateTo, setDateTo] = useState(
    new Date(requiredEducationPeriods[0].endDate),
  );

  const [isFiltersDependentDataFetched, setIsFiltersDependentDataFetched] =
    useState(false);
  const [parametersHaveChanged, setParametersHaveChanged] = useState(false);

  const transformedDistrictData = transformDistrictData(
    districtSchoolStudentHierarchyData,
  );
  const transFormedClassData = transFormClassData(
    sectionInitialState,
    extractedDistrictSchoolStudentHierarchyData,
    schoolSelected,
  );

  const transformedStudentsData = transFormStudentsData(
    studentsInitialState,
    extractedDistrictSchoolStudentHierarchyData,
    sectionSelected.value,
  );
  const transformedCategoryData = transformCategoryData(categories);
  const studentDistrictId =
    isStudent && schoolSet.length > 1
      ? schoolIdToDistrictIdMap[schoolSelected.value]
      : undefined;

  const filtersParamsData = {
    tagCategoryId: selectedCategory?.value,
    masterSubjectId: subjectId,
    educationPeriod: academicYear.value,
    districtId: districtSelected?.value || studentDistrictId,
    classIds: sectionSelected.value,
    studentId: studentSelected.value,
    dateFrom: dateTimeFormat(dateFrom, '', 'dateDatabaseFormat'),
    dateTo: dateTimeFormat(dateTo, '', 'dateDatabaseFormat'),
    questionType: questionTypeSelected.label,
    schoolIds: schoolSelected?.value,
  };

  const setUpFiltersData = async () => {
    const requiredEducationPeriods = getRequiredEducationPeriods(
      academicYear.value,
      educationPeriods,
      courseOutline?.earliestReportingEp || 0,
    );
    const currentEducationPeriodData =
      requiredEducationPeriods[requiredEducationPeriods.length - 1];
    const dateFromData = getPeriodStartDate(
      currentEducationPeriodData.contentAvailableAt,
    );
    const dateToData = new Date(currentEducationPeriodData.endDate);
    setDateFrom(dateFromData);
    setDateTo(dateToData);

    // Fetch and set DistrictSchoolStudentHierarchy data
    const districtSchoolStudentHierarchyData =
      await getDistrictSchoolStudentHierarchy({
        subjectId,
        educationPeriod: academicYear.value,
      });
    setDistrictSchoolStudentHierarchyData(districtSchoolStudentHierarchyData);
    const defaultDistrictSelected = transformDistrictData(
      districtSchoolStudentHierarchyData,
    )[0];
    setDistrictSelected(defaultDistrictSelected);

    // Fetch and set categories
    const categoryData = await getCategories(subjectId);
    setCategories(categoryData);
    const defaultSelectedCategory =
      transformCategoryData(categoryData).slice(-1)[0];
    setSelectedCategory(defaultSelectedCategory);
    setSelectedCurrCategory(defaultSelectedCategory.label);
    setIsFiltersDependentDataFetched(true);
    setSelectedAcademicYear(academicYear.value);
    filtersParamsData.tagCategoryId = defaultSelectedCategory.value;
    if (defaultDistrictSelected) {
      filtersParamsData.districtId =
        defaultDistrictSelected.value || studentDistrictId;
    }
    filtersParamsData.dateFrom = dateTimeFormat(
      dateFromData,
      '',
      'dateDatabaseFormat',
    );
    filtersParamsData.dateTo = dateTimeFormat(
      dateToData,
      '',
      'dateDatabaseFormat',
    );
    // districtId is required for non student user. we don't want to make request when it is not set.
    const isDistrictSelectedOrisStudentUser =
      filtersParamsData.districtId || isStudent;
    // Fetch Trend data
    if (previousEducationPeriod === 0 && isDistrictSelectedOrisStudentUser) {
      fetchTrendData(filtersParamsData);
      setParametersHaveChanged(false);
      setPreviousEducationPeriod(academicYear.value);
    }
  };

  useEffect(() => {
    setSelectedStudentName(studentSelected.label);
    setSelectedSectionName(sectionSelected.label);
  }, [studentSelected, sectionSelected]);

  useEffect(() => {
    setUpFiltersData();
  }, [academicYear.value]);

  useEffect(() => {
    if (academicYear.value !== 0) {
      setUpFiltersData();
    }
  }, []);

  const isMobileView = useDynamicColspan() === 2;

  useEffect(() => {
    if (isMobileView) {
      applyFilters();
    }
  }, [selectedCategory.value, questionTypeSelected.value]);

  // Re-select class/section data on district changed
  useEffect(() => {
    setSectionSelected(transFormedClassData[0]);
    setSchoolSelected(schoolList[0]);
  }, [districtSelected?.value]);

  // Re-select student data on class/section changed
  useEffect(() => {
    setStudentSelected(transformedStudentsData[0]);
  }, [sectionSelected.value]);

  // Re-fetch trend data on filters changed
  const applyFilters = () => {
    setSelectedCurrCategory(selectedCategory.label);
    fetchTrendData(filtersParamsData);
    setParametersHaveChanged(false);
  };

  // it is true if transformedDistrictData or categories are empty
  // and if isFiltersDependentDataFetched is false
  const shouldShowLoadingSpinner =
    (!transformedDistrictData.length || !categories.length) &&
    !isFiltersDependentDataFetched;
  // It ensures the filters are render after all the required filters data are fetched.
  // It should show on first page render only.
  // if any of dependent data is empty the filters will be rendered except the affected dropdown select.
  if (shouldShowLoadingSpinner) {
    return <LoadingSpinner />;
  }

  return (
    <Fragment>
      <div className="trend_report_filters_wrapper">
        {showAcademicYearsDropdown && (
          <AutocompleteSelect
            options={academicYears}
            onChange={({ value: valueSelected }) => {
              setParametersHaveChanged(true);
              setAcademicYear(
                academicYears.find(({ value }) => valueSelected === value),
              );
            }}
            value={academicYear}
            trackingId="academic-year-selector"
          />
        )}
        {!isStudent && transformedDistrictData.length > 1 && (
          <AutocompleteSelect
            options={transformedDistrictData}
            onChange={({ value: valueSelected }) => {
              setParametersHaveChanged(true);
              setDistrictSelected(
                transformedDistrictData.find(
                  ({ value }) => valueSelected === value,
                ),
              );
            }}
            value={districtSelected}
            trackingId="district-selector"
          />
        )}
        {showSchoolDropdown && (
          <AutocompleteSelect
            options={schoolList}
            onChange={({ value: valueSelected }) => {
              setParametersHaveChanged(true);
              setSchoolSelected(
                schoolList.find(({ value }) => valueSelected === value),
              );
            }}
            value={schoolSelected}
            trackingId="school-selector"
          />
        )}
        {!isStudent &&
          (transFormedClassData.length > 2 || showSchoolDropdown) && (
            <AutocompleteSelect
              options={transFormedClassData}
              onChange={({ value: valueSelected }) => {
                setParametersHaveChanged(true);
                setSectionSelected(
                  transFormedClassData.find(
                    ({ value }) => valueSelected === value,
                  ),
                );
              }}
              value={sectionSelected}
              trackingId="class-selector"
            />
          )}
        {!isStudent && (
          <AutocompleteSelect
            options={transformedStudentsData}
            onChange={({ value: valueSelected }) => {
              setParametersHaveChanged(true);
              setStudentSelected(
                transformedStudentsData.find(
                  ({ value }) => valueSelected === value,
                ),
              );
            }}
            value={studentSelected}
            placeholder={{ label: 'Select a Student' }}
            clearable={studentSelected}
            customReset={() => setStudentSelected(transformedStudentsData[0])}
            icon={
              studentSelected && studentSelected.label !== 'All Students' ? (
                <Icon
                  name="xmark"
                  onClick={() => setStudentSelected(studentsInitialState)}
                />
              ) : (
                <Icon className="HierarchyFilters__search-icon" name="search" />
              )
            }
            trackingId="student-selector"
          />
        )}
      </div>
      <div className="trend_report_filters_wrapper">
        <div className="trend_report_filter_group">
          {categories.length > 0 && (
            <Fragment>
              <div className="dropdown_label">Course Component</div>
              <div className="auto-select-wrapper">
                <label className="text-gray-600">Curriculum Category</label>
                <AutocompleteSelect
                  className="w-max"
                  options={transformedCategoryData}
                  onChange={({ value: valueSelected }) => {
                    setParametersHaveChanged(true);
                    setSelectedCategory(
                      transformedCategoryData.find(
                        ({ value }) => valueSelected === value,
                      ),
                    );
                  }}
                  value={selectedCategory}
                  trackingId="category-selector"
                />
              </div>
            </Fragment>
          )}
          <div className="auto-select-wrapper">
            <label className="text-gray-600">Question Type</label>
            <AutocompleteSelect
              className="w-max"
              options={questionTypes}
              onChange={({ value: valueSelected }) => {
                setParametersHaveChanged(true);
                setQuestionTypeSelected(
                  questionTypes.find(({ value }) => valueSelected === value),
                );
              }}
              value={questionTypeSelected}
              trackingId="question-type-selector"
            />
          </div>
        </div>
        <div className="trend_report_filter_group trend_report_filter_date">
          <div className="trend_report_date_and_button_wrapper">
            <div className="trend_report_group_date_wrapper">
              <div>Submit Date Range</div>
              <DatePickerWrapper
                defaultValue={dateFrom}
                onChange={(value) => {
                  setParametersHaveChanged(true);
                  setDateFrom(value);
                }}
                minDate={isStudent ? dateFrom : null}
                trackingId="date-from-selector"
              />
              <div>to</div>
              <DatePickerWrapper
                defaultValue={dateTo}
                onChange={(value) => {
                  setParametersHaveChanged(true);
                  setDateTo(value);
                }}
                minDate={isStudent ? dateFrom : null}
                trackingId="date-to-selector"
              />
            </div>
            <Button
              key="yes-button"
              isPrimary
              className="ml-0.6"
              onClick={() => applyFilters()}
              disabled={!parametersHaveChanged}
            >
              Apply
            </Button>
          </div>
        </div>
        <FiltersWithPopover
          dateFrom={dateFrom}
          setParametersHaveChanged={setParametersHaveChanged}
          setDateTo={setDateTo}
          setDateFrom={setDateFrom}
          isStudent={isStudent}
          dateTo={dateTo}
          router={router}
          updateAssessmentTypeParams={updateAssessmentTypeParams}
          checkedItems={checkedItems}
          setCheckedItems={setCheckedItems}
          applyFilters={applyFilters}
          parametersHaveChanged={parametersHaveChanged}
        />
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  const { userData } = state;
  const { educationPeriod, isStudent, me, teacherSubjects } = userData;
  const { educationPeriods, currentEducationPeriod } = educationPeriod;
  const { schoolSet } = me;
  return {
    educationPeriods,
    currentEducationPeriod,
    isStudent,
    schoolSet,
    teacherSubjects,
  };
};

export default connect(mapStateToProps)(Filters);
